import React, { useState, useEffect } from 'react';
import { Alert, Button, ButtonGroup, Container, Row, Col } from 'react-bootstrap';
import { consentManager } from '../../services/ConsentManager';
import { ConsentSettings } from '../../types/analytics';

interface ConsentBannerProps {
    onConsentChange?: (consent: ConsentSettings) => void;
}

/**
 * Privacy consent banner component
 * Displays privacy notice and consent options for analytics tracking
 * Integrates with ConsentManager for consent storage and management
 * @param {ConsentBannerProps} props - Component props
 * @return {JSX.Element|null} Consent banner component or null if not shown
 */
export function ConsentBanner({ onConsentChange }: ConsentBannerProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if consent banner should be shown
    const shouldShow = consentManager.shouldShowConsentBanner();
    setShowBanner(shouldShow);

    // Listen for consent changes
    const handleConsentChange = (consent: ConsentSettings) => {
      // Hide banner once user makes a choice
      setShowBanner(false);
      onConsentChange?.(consent);
    };

    consentManager.addConsentListener(handleConsentChange);

    // Cleanup listener on unmount
    return () => {
      consentManager.removeConsentListener(handleConsentChange);
    };
  }, [onConsentChange]);

  const handleAcceptAll = async () => {
    setIsLoading(true);
    try {
      consentManager.grantAllConsent();
    } catch (error) {
      console.error('ConsentBanner: Error accepting all consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptEssential = async () => {
    setIsLoading(true);
    try {
      consentManager.grantEssentialConsent();
    } catch (error) {
      console.error('ConsentBanner: Error accepting essential consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeclineAll = async () => {
    setIsLoading(true);
    try {
      consentManager.denyAllConsent();
    } catch (error) {
      console.error('ConsentBanner: Error declining all consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if banner shouldn't be shown
  if (!showBanner) {
    return null;
  }

  return (
    <Alert
      variant="info"
      className="consent-banner"
      style={{
        position: 'fixed',
        bottom: '80px', // Above footer
        left: '0',
        right: '0',
        zIndex: 1050,
        margin: '0',
        borderRadius: '0',
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: 'none',
      }}
    >
      <Container fluid>
        <Row className="align-items-center">
          <Col md={8} className="mb-2 mb-md-0">
            <div>
              <strong>Privacy Notice</strong>
              <p className="mb-0 small">
                                We use analytics to improve your experience with the Sailing Handicap Calculator.
                                This helps us understand which features are most valuable and optimize the application.
                                You can opt out at any time. No personally identifiable information is collected.
              </p>
            </div>
          </Col>
          <Col md={4}>
            <ButtonGroup size="sm" className="w-100 d-flex">
              <Button
                variant="success"
                onClick={handleAcceptAll}
                disabled={isLoading}
                className="flex-fill"
              >
                {isLoading ? 'Saving...' : 'Accept All'}
              </Button>
              <Button
                variant="primary"
                onClick={handleAcceptEssential}
                disabled={isLoading}
                className="flex-fill"
              >
                                Essential Only
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleDeclineAll}
                disabled={isLoading}
                className="flex-fill"
              >
                                Decline
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </Container>
    </Alert>
  );
}

export default ConsentBanner;
